import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../api/api";
import blogDeaultImage from "../../image/default blog.png";
import { gsap } from "../../animations/gsap.config";
import {
  Clock,
  Eye,
  Heart,
  Tag,
  ArrowLeft,
  User,
  Calendar,
  Share2,
  BookOpen,
} from "lucide-react";
import toast from "react-hot-toast";
import SEO from "../SEO";

const BlogDetail = () => {
  const { slug } = useParams();
  const queryClient = useQueryClient();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  const headerRef = useRef(null);
  const contentRef = useRef(null);
  const sidebarRef = useRef(null);

  // Tracks if the user has clicked like during this view session
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);

  /* ─────────────────────────────────────────────
     FETCH SINGLE BLOG BY SLUG AS PARAMETER ID
  ───────────────────────────────────────────── */
  const { data: postData, isLoading: isPostLoading } = useQuery({
    queryKey: ["blog", slug],
    queryFn: async () => {
      const response = await api.get(`/api/blogs/public/${slug}`);
      return response.data;
    },
  });

  const post = postData?.blog || postData;

  /* ─────────────────────────────────────────────
     FETCH ALL PUBLIC BLOGS FOR SIDEBAR MATCHING
  ───────────────────────────────────────────── */
  const { data: relatedData } = useQuery({
    queryKey: ["blogs"],
    queryFn: async () => {
      const response = await api.get("/api/blogs/public", {
        params: { limit: 100 }, // Fetches active articles pool to match categories
      });
      return response.data;
    },
  });

  const allPosts = relatedData?.blogs || [];

  // Filter to find blogs with the same category that are not the current active post
  const relatedPosts = allPosts
    .filter((p) => p.category === post?.category && p.id !== post?.id)
    .slice(0, 3);

  /* ─────────────────────────────────────────────
     LIKE MUTATION HANDLER
  ───────────────────────────────────────────── */
  const likeMutation = useMutation({
    mutationFn: async () => {
      const response = await api.patch(`/blog/public/${slug}/like`);
      return response.data;
    },
    onSuccess: (data) => {
      setLikesCount(data.likes);
      setIsLiked(true);
      toast.success("Blog Liked");
      // Invalidate query to keep shared background values fresh
      queryClient.invalidateQueries(["blog", slug]);
    },
    onError: (err) => {
      toast.error("failed to like Blog");
    },
  });

  // Sync state values cleanly once server response resolves successfully
  useEffect(() => {
    if (post) {
      setLikesCount(post.likes || 0);
    }
  }, [post]);

  /* GSAP entrance sequences */
  useEffect(() => {
    if (!post) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: "power3.out", delay: 0.2 },
      );

      gsap.fromTo(
        contentRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: "power3.out", delay: 0.4 },
      );

      if (sidebarRef.current) {
        gsap.fromTo(
          sidebarRef.current,
          { x: 30, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.8, ease: "power3.out", delay: 0.6 },
        );
      }
    });

    return () => ctx.revert();
  }, [post]);

  if (isPostLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-sm font-semibold text-gray-500 animate-pulse">
          Loading article content...
        </p>
      </div>
    );
  }

  if (!post || !post.title) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt}
        image={post.image ? `${backendUrl}${post.image}` : blogDeaultImage}
        keywords={post.tags}
        url={window.location.href}
      />
      <main className="relative overflow-hidden">
        {/* Background layer */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 50%, #f8fafc 100%)",
            }}
          />
          <div className="absolute inset-0 dot-pattern opacity-[0.05]" />
          <div
            className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(29,78,216,0.08) 0%, transparent 70%)",
              filter: "blur(80px)",
            }}
          />
        </div>

        <div className="container-fluid relative z-10 pt-72 pb-20">
          <div className="max-w-4xl mx-auto">
            {/* Back Button Link Trigger */}
            <div className="mb-8">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300 transition-colors duration-300 text-left"
              >
                <ArrowLeft size={16} />
                <span className="text-sm font-medium">Back to Blog</span>
              </Link>
            </div>

            {/* Article Header Content Context Wrapper */}
            <header ref={headerRef} className="mb-12" style={{ opacity: 0 }}>
              {/* Category Pill */}
              <div className="mb-4">
                <span
                  className="inline-flex items-center gap-1.5 text-xs font-bold tracking-wider uppercase px-3 py-1.5 rounded-full"
                  style={{
                    color: post.category === "Career" ? "#f59e0b" : "#3b82f6",
                    background:
                      post.category === "Career"
                        ? "rgba(245,158,11,0.2)"
                        : "rgba(59,130,246,0.2)",
                    border: `1px solid ${
                      post.category === "Career"
                        ? "rgba(245,158,11,0.4)"
                        : "rgba(59,130,246,0.4)"
                    }`,
                  }}
                >
                  <Tag size={10} />
                  {post.category}
                </span>
              </div>

              {/* Main Title */}
              <h1 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight mb-6 font-display">
                {post.title}
              </h1>

              {/* Meta Info Row */}
              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600 mb-8">
                <div className="flex items-center gap-2">
                  <User size={14} className="text-primary-400" />
                  <span>{post.author?.name}</span>
                  <span className="text-slate-600">•</span>
                  <span>{post.author?.role}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={14} className="text-primary-400" />
                  <time dateTime={post.dateISO}>{post.date}</time>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-primary-400" />
                  <span>{post.readTime}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Eye size={14} className="text-primary-400" />
                  <span>{post.views}</span>
                </div>
              </div>

              {/* Featured Image */}
              <div className="relative rounded-2xl overflow-hidden mb-8">
                <img
                  src={
                    post.image ? `${backendUrl}${post.image}` : blogDeaultImage
                  }
                  alt={post.title}
                  className="w-full h-64 md:h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
              {/* Article Content Area */}
              <article
                ref={contentRef}
                className="lg:col-span-3"
                style={{ opacity: 0 }}
              >
                {/* Renders pure safe HTML fields seamlessly with Tailwind Typography styles */}
                <div
                  dangerouslySetInnerHTML={{ __html: post.content }}
                  className="prose prose-lg max-w-none text-gray-700 space-y-4 data-html-content"
                />

                {/* Author Biography Card */}
                <div className="mt-12 p-6 rounded-2xl bg-gray-100 border border-gray-200">
                  <div className="flex items-start gap-4">
                    {post.author?.avatar ? (
                      <img
                        src={post.author.avatar}
                        alt={post.author.name}
                        className="w-16 h-16 rounded-xl object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-gray-200 text-gray-400">
                        <User size={32} />
                      </div>
                    )}
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-1">
                        {post.author?.name}
                      </h4>
                      <p className="text-primary-600 text-sm mb-2">
                        {post.author?.role}
                      </p>
                      <p className="text-gray-600 text-sm">
                        {post.author?.name} is a key member of the team,
                        bringing extensive experience in{" "}
                        {post.category?.toLowerCase()} and development work.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Social Sharing & Like Counter Blocks */}
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-600">
                        Share this article:
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            if (navigator.share) {
                              navigator.share({
                                title: post.title,
                                text: post.excerpt,
                                url: window.location.href,
                              });
                            } else {
                              navigator.clipboard.writeText(
                                window.location.href,
                              );
                            }
                          }}
                          className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors duration-300"
                          title="Share article"
                        >
                          <Share2 size={16} className="text-gray-600" />
                        </button>
                      </div>
                    </div>

                    {/* Like Button Wrapper (Disables permanently upon interaction click) */}
                    <div className="flex items-center gap-2">
                      <button
                        disabled={isLiked || likeMutation.isPending}
                        onClick={(e) => {
                          e.preventDefault();
                          gsap.fromTo(
                            e.currentTarget,
                            { scale: 0.8 },
                            { scale: 1, duration: 0.4, ease: "back.out(2)" },
                          );
                          likeMutation.mutate();
                        }}
                        className={`flex items-center gap-1 transition-all duration-300 ${
                          isLiked
                            ? "text-red-500 cursor-not-allowed opacity-75"
                            : "text-gray-600 hover:text-red-500"
                        }`}
                        title={
                          isLiked
                            ? "You liked this article"
                            : "Like this article"
                        }
                      >
                        <Heart
                          size={16}
                          className={
                            isLiked
                              ? "text-red-500 fill-current"
                              : "text-gray-600"
                          }
                          fill={isLiked ? "currentColor" : "none"}
                        />
                      </button>
                      <span className="text-sm text-gray-600">
                        {likesCount} likes
                      </span>
                    </div>
                  </div>
                </div>
              </article>

              {/* Sidebar Column */}
              <aside
                ref={sidebarRef}
                className="lg:col-span-1"
                style={{ opacity: 0 }}
              >
                <div className="sticky top-24 space-y-6">
                  {/* Related Articles List Filtered out by shared Category */}
                  <div className="p-5 rounded-2xl bg-gray-100 border border-gray-200">
                    <div className="flex items-center gap-2 mb-4">
                      <BookOpen size={14} className="text-primary-400" />
                      <h4 className="text-sm font-bold text-gray-900">
                        Related Articles
                      </h4>
                    </div>
                    <div className="space-y-3">
                      {relatedPosts.length > 0 ? (
                        relatedPosts.map((relatedPost) => (
                          <Link
                            key={relatedPost.id}
                            to={relatedPost.href}
                            className="block group"
                          >
                            <div className="flex gap-3">
                              <img
                                src={relatedPost.image}
                                alt={relatedPost.title}
                                className="w-12 h-12 rounded-lg object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-900 font-medium line-clamp-2 group-hover:text-primary-600 transition-colors duration-300">
                                  {relatedPost.title}
                                </p>
                                <p className="text-[10px] text-gray-500 mt-1">
                                  {relatedPost.date}
                                </p>
                              </div>
                            </div>
                          </Link>
                        ))
                      ) : (
                        <p className="text-xs text-gray-500 italic">
                          No related articles found in this category.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default BlogDetail;
